import React, { useCallback, useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import {
    ArrowRight,
    Mail,
    Search,
    AlertCircle,
    AlertTriangle,
    Activity,
    BookmarkCheck,
    X,
} from "lucide-react";
import { lookupSnapshotsByEmail } from "../lib/api";
import { isValidEmail } from "../lib/validation";
import { Button } from "../components/shared/Button";
import { Logo } from "../components/shared/Logo";
import { Footer } from "../components/shared/Footer";
import { Pill } from "../admin/components/Pill";

const SURVEY_PREFILL_KEY = "klareet_survey_email_prefill";

function fmtDate(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleDateString(undefined, {
            year: "numeric",
            month: "short",
            day: "numeric",
        });
    } catch {
        return iso.slice(0, 10);
    }
}

export default function Returning() {
    const navigate = useNavigate();
    const location = useLocation();

    // One-shot: read router state on first render, then strip it from
    // history so a refresh lands on a clean state.
    const initialState = location.state || null;
    const [email, setEmail] = useState(initialState?.email || "");
    const [flashMessage, setFlashMessage] = useState(
        initialState?.flash || null,
    );
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [result, setResult] = useState(null);

    const runLookup = useCallback(async (raw) => {
        const value = (raw || "").trim();
        setError(null);
        if (!isValidEmail(value) || !value) {
            setError("That email looks off. Check the format and try again.");
            setResult(null);
            return;
        }
        setLoading(true);
        const res = await lookupSnapshotsByEmail(value);
        setLoading(false);
        if (res.ok) {
            setResult(res.data);
        } else if (res.rateLimited) {
            setError(
                "Too many lookup attempts. Try again in an hour or contact klair@klareet.com.",
            );
            setResult(null);
        } else {
            setError("We couldn't run the lookup. Try again in a moment.");
            setResult(null);
        }
    }, []);

    // On mount: if router state handed us an email, auto-run the lookup, then
    // strip the state so a hard-refresh produces a clean page.
    useEffect(() => {
        if (initialState?.email) {
            runLookup(initialState.email);
        }
        if (initialState) {
            // Replace history entry without state so refresh = blank page.
            navigate(location.pathname, { replace: true, state: null });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    function onSubmit(e) {
        e.preventDefault();
        runLookup(email);
    }

    const remaining = result?.attempts_remaining ?? null;
    const snapshots = result?.snapshots ?? [];

    return (
        <div className="relative min-h-screen klareet-grain klareet-glow flex flex-col">
            <header className="relative z-10 px-5 md:px-12 pt-6 pb-2 flex items-center justify-between">
                <Link to="/" className="inline-flex">
                    <Logo className="h-14 md:h-16 w-auto" />
                </Link>
                <div className="hidden md:block text-[11px] uppercase tracking-[0.22em] font-mono text-klareet-T2">
                    Returning · find your snapshot
                </div>
            </header>

            <main className="relative z-10 flex-1 px-5 md:px-12 py-10 max-w-3xl w-full mx-auto">
                <AnimatePresence>
                    {flashMessage ? (
                        <motion.div
                            key="returning-flash-banner"
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            transition={{ duration: 0.3 }}
                            role="alert"
                            className="mb-8 rounded-2xl border border-klareet-AMBER/45 bg-klareet-NAVY/70 px-5 py-4 flex items-start gap-3"
                            data-testid="returning-flash-banner"
                        >
                            <AlertTriangle
                                size={18}
                                className="text-klareet-AMBER mt-0.5 shrink-0"
                            />
                            <div className="flex-1 text-sm md:text-base text-klareet-AMBER leading-relaxed">
                                {flashMessage}
                            </div>
                            <button
                                type="button"
                                onClick={() => setFlashMessage(null)}
                                className="text-klareet-AMBER/80 hover:text-klareet-TEXT shrink-0 transition-colors"
                                aria-label="Dismiss"
                                data-testid="returning-flash-dismiss"
                            >
                                <X size={16} />
                            </button>
                        </motion.div>
                    ) : null}
                </AnimatePresence>

                <div className="text-center mb-10">
                    <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-semibold text-klareet-T">
                        <Activity size={14} />
                        Returning visitor
                    </div>
                    <h1
                        className="mt-4 text-4xl md:text-5xl font-bold tracking-tight text-klareet-TEXT"
                        data-testid="returning-headline"
                    >
                        View your snapshot
                    </h1>
                    <p className="mt-4 text-klareet-MUT max-w-lg mx-auto leading-relaxed">
                        Enter the email you used. We'll bring up your past
                        snapshot.
                    </p>
                </div>

                <form
                    onSubmit={onSubmit}
                    className="rounded-3xl border border-klareet-bor bg-klareet-card p-6 md:p-8 space-y-4"
                    data-testid="returning-form"
                >
                    <label className="block">
                        <div className="text-[11px] uppercase tracking-[0.18em] text-klareet-MUT font-mono mb-2">
                            Email
                        </div>
                        <div className="relative">
                            <Mail
                                size={16}
                                className="absolute left-4 top-1/2 -translate-y-1/2 text-klareet-MUT"
                            />
                            <input
                                type="email"
                                autoComplete="email"
                                inputMode="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="you@example.com"
                                className="w-full pl-11 pr-4 py-3.5 rounded-xl border border-klareet-bor bg-klareet-NAVY text-klareet-TEXT placeholder:text-klareet-DIM outline-none focus:border-klareet-T focus:ring-2 focus:ring-klareet-T/40 transition-colors"
                                data-testid="returning-email-input"
                                autoFocus
                            />
                        </div>
                    </label>

                    {error ? (
                        <div
                            className="text-sm text-klareet-RED font-mono flex items-center gap-2"
                            data-testid="returning-error"
                        >
                            <AlertCircle size={14} />
                            {error}
                        </div>
                    ) : null}

                    <Button
                        size="lg"
                        type="submit"
                        disabled={loading}
                        className="w-full"
                        data-testid="returning-submit-btn"
                    >
                        {loading ? (
                            <>
                                <span className="inline-block w-4 h-4 rounded-full border-2 border-white/40 border-t-white animate-spin" />
                                Looking…
                            </>
                        ) : (
                            <>
                                <Search size={16} />
                                Find my snapshot
                            </>
                        )}
                    </Button>
                </form>

                <div className="mt-8" aria-live="polite">
                    {result && snapshots.length === 0 ? (
                        <EmptyResult onTakeSurvey={() => navigate("/survey")} />
                    ) : null}

                    {result && snapshots.length > 0 ? (
                        <ResultsList
                            snapshots={snapshots}
                            remaining={remaining}
                            onTakeAgain={() => {
                                try {
                                    sessionStorage.setItem(
                                        SURVEY_PREFILL_KEY,
                                        email.trim().toLowerCase(),
                                    );
                                } catch {
                                    // ignore
                                }
                                navigate("/survey?fresh=true");
                            }}
                        />
                    ) : null}
                </div>
            </main>

            <Footer />
        </div>
    );
}

function EmptyResult({ onTakeSurvey }) {
    return (
        <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="rounded-3xl border border-klareet-bor bg-klareet-card p-8 text-center"
            data-testid="returning-empty"
        >
            <BookmarkCheck size={28} className="text-klareet-MUT mx-auto" />
            <div className="mt-4 text-lg font-semibold tracking-tight text-klareet-TEXT">
                No snapshot found for that email
            </div>
            <p className="mt-2 text-klareet-MUT text-sm leading-relaxed max-w-md mx-auto">
                Take the survey to get yours. 10 questions, 4 minutes, and you
                walk away with a personal glimpse.
            </p>
            <div className="mt-6 flex justify-center">
                <Button
                    size="md"
                    onClick={onTakeSurvey}
                    data-testid="returning-empty-survey-btn"
                >
                    Take the survey
                    <ArrowRight size={16} />
                </Button>
            </div>
        </motion.div>
    );
}

function ResultsList({ snapshots, remaining, onTakeAgain }) {
    return (
        <div className="space-y-5" data-testid="returning-results">
            <div className="text-[11px] uppercase tracking-[0.18em] font-mono text-klareet-MUT">
                {snapshots.length === 1
                    ? "1 snapshot"
                    : `${snapshots.length} snapshots`}
            </div>
            <div className="space-y-4">
                {snapshots.map((snap, idx) => (
                    <SnapshotCard
                        key={snap.response_id}
                        snap={snap}
                        index={idx}
                    />
                ))}
            </div>

            {remaining != null ? (
                <div
                    className={`mt-6 rounded-2xl border px-5 py-4 flex flex-col sm:flex-row sm:items-center gap-3 ${
                        remaining > 0
                            ? "border-klareet-T/40 bg-klareet-T/5"
                            : "border-klareet-AMBER/40 bg-klareet-AMBER/5"
                    }`}
                    data-testid="returning-attempts-banner"
                >
                    <div className="flex-1 text-sm">
                        <span
                            className={`font-mono ${
                                remaining > 0
                                    ? "text-klareet-T2"
                                    : "text-klareet-AMBER"
                            }`}
                        >
                            Attempts remaining:{" "}
                            <span
                                className="font-semibold"
                                data-testid="returning-attempts-count"
                            >
                                {remaining} of 2
                            </span>
                        </span>
                        <div className="text-klareet-MUT mt-1">
                            {remaining > 0
                                ? "Want to try a different angle? Take the survey again with this email and we'll cap it at 2."
                                : "You've used both attempts. Your most recent snapshot is shown above."}
                        </div>
                    </div>
                    {remaining > 0 ? (
                        <Button
                            size="sm"
                            onClick={onTakeAgain}
                            data-testid="returning-take-again-btn"
                        >
                            Take it again
                            <ArrowRight size={14} />
                        </Button>
                    ) : null}
                </div>
            ) : null}
        </div>
    );
}

function SnapshotCard({ snap, index }) {
    const dashUrl = `/r/${snap.share_code}`;
    const viewUrl = `/snapshot/${snap.response_id}?code=${snap.share_code}`;
    return (
        <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: index * 0.05 }}
            className="rounded-3xl border border-klareet-bor bg-klareet-card p-5 md:p-6 flex flex-col md:flex-row md:items-center gap-5"
            data-testid={`returning-snapshot-${snap.response_id}`}
        >
            <div className="flex items-center gap-4 flex-1 min-w-0">
                <div
                    className="text-3xl md:text-4xl shrink-0"
                    aria-hidden
                    style={{ color: snap.archetype.color }}
                >
                    {snap.archetype.emoji}
                </div>
                <div className="min-w-0">
                    <div
                        className="text-lg md:text-xl font-semibold tracking-tight truncate"
                        style={{ color: snap.archetype.color }}
                    >
                        {snap.archetype.name}
                    </div>
                    <div className="text-xs font-mono text-klareet-MUT mt-0.5">
                        {fmtDate(snap.created_at)}
                    </div>
                    <div className="mt-2 flex items-center gap-2 flex-wrap">
                        <Pill value={snap.persona} kind="persona" />
                        <Pill value={snap.pain} kind="pain" />
                    </div>
                </div>
            </div>
            <div className="flex md:flex-col items-center gap-4 md:gap-2 md:items-end">
                <div className="text-right">
                    <div className="text-[10px] uppercase tracking-[0.18em] font-mono text-klareet-MUT">
                        Score
                    </div>
                    <div
                        className="font-mono text-2xl md:text-3xl font-semibold tabular-nums"
                        style={{
                            color:
                                snap.score >= 70
                                    ? "#10B981"
                                    : snap.score >= 50
                                      ? "#F59E0B"
                                      : "#EF4444",
                        }}
                    >
                        {snap.score}
                        <span className="text-klareet-MUT text-sm">/100</span>
                    </div>
                </div>
            </div>
            <div className="flex flex-col sm:flex-row md:flex-col gap-2 md:gap-2 md:w-44 shrink-0">
                <Link
                    to={viewUrl}
                    className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-full bg-klareet-T text-white text-sm font-medium hover:brightness-110 transition-all"
                    data-testid={`returning-view-${snap.response_id}`}
                >
                    View snapshot
                    <ArrowRight size={14} />
                </Link>
                <Link
                    to={dashUrl}
                    className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-full border border-klareet-bor bg-transparent text-klareet-MUT hover:text-klareet-TEXT hover:border-klareet-T text-sm transition-colors"
                    data-testid={`returning-dashboard-${snap.response_id}`}
                >
                    Open dashboard
                </Link>
            </div>
        </motion.div>
    );
}
